/**
 * The board model: 81 cells, digits 1-9, 0 for empty.
 *
 * This is deliberately the smallest possible representation -- a flat
 * Uint8Array -- because it is what the solvers, the generator and the grader
 * all pass around, thousands of times per generated puzzle. Pencil marks,
 * given/entered distinction and the move log live in `state/game.ts`; keeping
 * them out of here is what lets `core/` stay pure and cheap.
 */

export const SIZE = 9;
export const CELLS = SIZE * SIZE;

export type Digit = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

/** 81 entries, index = row * 9 + col, value 0 (empty) or 1-9. */
export type Board = Uint8Array;

export const EMPTY = 0;

export function emptyBoard(): Board {
  return new Uint8Array(CELLS);
}

export function cloneBoard(board: Board): Board {
  return Uint8Array.from(board);
}

export function rowOf(cell: number): number {
  return (cell / SIZE) | 0;
}

export function colOf(cell: number): number {
  return cell % SIZE;
}

export function boxOf(cell: number): number {
  return ((rowOf(cell) / 3) | 0) * 3 + ((colOf(cell) / 3) | 0);
}

export function cellAt(row: number, col: number): number {
  return row * SIZE + col;
}

/** "r4c7" -- the notation used in hint text and technique explanations. */
export function cellName(cell: number): string {
  return `r${rowOf(cell) + 1}c${colOf(cell) + 1}`;
}

const EMPTY_CHARS = new Set([".", "0", "-", " ", "_"]);

/**
 * Parse an 81-character puzzle string. `.`, `0`, `-`, `_` and spaces all mean
 * empty; newlines and `|`/`+` grid art are ignored, so puzzles pasted from
 * forums and books work without cleanup.
 */
export function boardFromString(text: string): Board {
  const board = emptyBoard();
  let cell = 0;
  for (const ch of text) {
    if (ch === "\n" || ch === "\r" || ch === "|" || ch === "+") continue;
    if (cell >= CELLS) throw new Error(`puzzle string has more than ${CELLS} cells`);
    if (EMPTY_CHARS.has(ch)) {
      board[cell++] = EMPTY;
    } else if (ch >= "1" && ch <= "9") {
      board[cell++] = ch.charCodeAt(0) - 48;
    } else {
      throw new Error(`unexpected character ${JSON.stringify(ch)} in puzzle string`);
    }
  }
  if (cell !== CELLS) throw new Error(`puzzle string has ${cell} cells, expected ${CELLS}`);
  return board;
}

export function boardToString(board: Board, emptyChar = "."): string {
  let out = "";
  for (let i = 0; i < CELLS; i++) out += board[i] === EMPTY ? emptyChar : String(board[i]);
  return out;
}

/** Multi-line rendering with box rules -- for test failure output and debugging. */
export function boardToPretty(board: Board): string {
  const lines: string[] = [];
  for (let r = 0; r < SIZE; r++) {
    if (r > 0 && r % 3 === 0) lines.push("------+-------+------");
    let line = "";
    for (let c = 0; c < SIZE; c++) {
      if (c > 0 && c % 3 === 0) line += "| ";
      const v = board[cellAt(r, c)];
      line += (v === EMPTY ? "." : String(v)) + " ";
    }
    lines.push(line.trimEnd());
  }
  return lines.join("\n");
}

export function clueCount(board: Board): number {
  let n = 0;
  for (let i = 0; i < CELLS; i++) if (board[i] !== EMPTY) n++;
  return n;
}

export function isComplete(board: Board): boolean {
  for (let i = 0; i < CELLS; i++) if (board[i] === EMPTY) return false;
  return true;
}
